<template>
  <transition name="teclado-login">
    <div v-if="abierto" class="teclado-login" @mousedown.prevent @touchstart.stop>
      <div class="teclado-login__barra">
        <span class="teclado-login__campo">{{ etiqueta }}</span>
        <v-btn text small color="success" @click="$emit('cerrar')">
          <v-icon small left>mdi-keyboard-close</v-icon>
          Ocultar
        </v-btn>
      </div>
      <div class="teclado-login__teclas"><div ref="teclado" class="simple-keyboard"></div></div>
    </div>
  </transition>
</template>

<script>
import Keyboard from "simple-keyboard";
import "simple-keyboard/build/css/index.css";

/**
 * Teclado en pantalla del login para los POS táctiles sin teclado. Va fijo abajo (el login es corto, el
 * formulario se sube para que no quede tapado) y escribe en el campo que esté activo. No es el
 * `SimpleKeyboard` del resto del POS: ese sale como menú junto al campo y aquí taparía la contraseña.
 */
export default {
  name: "TecladoLogin",
  props: {
    abierto: { type: Boolean, default: false },
    valor: { type: String, default: "" },
    etiqueta: { type: String, default: "" },
    // «Siguiente» en el correo, «Ingresar» en la contraseña.
    accion: { type: String, default: "Siguiente" },
  },
  data: () => ({ teclado: null }),
  watch: {
    abierto(abierto) {
      if (abierto) {
        this.$nextTick(this.crear);
      } else if (this.teclado) {
        this.teclado.destroy();
        this.teclado = null;
      }
    },
    // Se escribió con otro medio (teclado físico, pegar): el teclado parte desde ahí.
    valor(valor) {
      if (this.teclado && this.teclado.getInput() !== valor) {
        this.teclado.setInput(valor);
      }
    },
  },
  beforeDestroy() {
    if (this.teclado) {
      this.teclado.destroy();
    }
  },
  methods: {
    crear() {
      if (!this.$refs.teclado || this.teclado) {
        return;
      }
      this.teclado = new Keyboard(this.$refs.teclado, {
        onChange: (texto) => this.$emit("cambio", texto),
        onKeyPress: this.alPulsar,
        layout: {
          default: [
            "1 2 3 4 5 6 7 8 9 0 {bksp}",
            "q w e r t y u i o p",
            "a s d f g h j k l ñ",
            "{shift} z x c v b n m . -",
            "{simbolos} @ {space} _ {enter}",
          ],
          shift: [
            "1 2 3 4 5 6 7 8 9 0 {bksp}",
            "Q W E R T Y U I O P",
            "A S D F G H J K L Ñ",
            "{shift} Z X C V B N M . -",
            "{simbolos} @ {space} _ {enter}",
          ],
          simbolos: [
            "! \" # $ % & / ( ) = {bksp}",
            "? ¿ ¡ * + - _ . , ;",
            "@ : ' < > [ ] { } ~",
            "^ | \\ ` ´ ¨ € ° %",
            "{letras} @ {space} _ {enter}",
          ],
        },
        display: {
          "{bksp}": "⌫",
          "{shift}": "⇧",
          "{space}": " ",
          "{simbolos}": "?123",
          "{letras}": "ABC",
          "{enter}": this.accion,
        },
        buttonTheme: [{ class: "teclado-login__accion", buttons: "{enter}" }],
        preventMouseDownDefault: true,
      });
      this.teclado.setInput(this.valor);
    },
    alPulsar(tecla) {
      if (tecla === "{enter}") {
        this.$emit("accion");
      } else if (tecla === "{shift}") {
        this.cambiarLayout(this.teclado.options.layoutName === "shift" ? "default" : "shift");
      } else if (tecla === "{simbolos}") {
        this.cambiarLayout("simbolos");
      } else if (tecla === "{letras}") {
        this.cambiarLayout("default");
      }
    },
    cambiarLayout(nombre) {
      this.teclado.setOptions({ layoutName: nombre });
    },
  },
  updated() {
    // La acción cambia de «Siguiente» a «Ingresar» al pasar de un campo al otro.
    if (this.teclado) {
      this.teclado.setOptions({ display: { ...this.teclado.options.display, "{enter}": this.accion } });
    }
  },
};
</script>

<style lang="scss">
.teclado-login {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 300;
  padding: 2px 10px 8px;
  background: #e9edf1;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.25);
}

.teclado-login__barra {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.teclado-login__campo {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
}

.teclado-login__teclas {
  max-width: 900px;
  margin: 0 auto;
}

.teclado-login__teclas .simple-keyboard {
  max-width: 900px;
  margin: 0 auto;
  background: transparent;
  padding: 0;
}

.teclado-login__teclas .hg-button {
  height: 44px;
  font-size: 1.1rem;
  border-radius: 8px;
}

.teclado-login__teclas .hg-button[data-skbtn="{space}"] {
  flex-grow: 6;
}

.teclado-login__teclas .hg-button.teclado-login__accion {
  background: #4caf50;
  color: #fff;
  font-weight: 600;
  flex-grow: 2;
}

.teclado-login-enter-active,
.teclado-login-leave-active {
  transition: transform 0.18s ease;
}

.teclado-login-enter,
.teclado-login-leave-to {
  transform: translateY(100%);
}
</style>
